// src/components/LandingTour/LandingTour.js
// The landing page guides visitors through itself, using the same ring,
// "Michii says" bubble and playback panel the extension shows on real sites.
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import ReactGA from 'react-ga4';
import Mascot from '../Mascot';
import '../../styles/components/LandingTour.css';

const DISMISS_KEY = 'michii_landing_tour_dismissed';
const PAD = 8;

function readDismissed() {
  try { return localStorage.getItem(DISMISS_KEY) === '1'; } catch (_) { return false; }
}
function writeDismissed(v) {
  try { v ? localStorage.setItem(DISMISS_KEY, '1') : localStorage.removeItem(DISMISS_KEY); } catch (_) {}
}

const LandingTour = ({ steps }) => {
  const [open, setOpen] = useState(() => !readDismissed());
  const [i, setI] = useState(0);
  const [box, setBox] = useState(null);
  const [drawKey, setDrawKey] = useState(0);
  const reduce = useRef(
    typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
  );

  const place = useCallback(() => {
    const el = document.querySelector(steps[i]?.selector);
    if (!el) { setBox(null); return; }
    const r = el.getBoundingClientRect();
    const top = r.top + window.scrollY - PAD;
    const left = r.left + window.scrollX - PAD;
    const width = r.width + PAD * 2;
    const height = r.height + PAD * 2;
    const bw = Math.min(320, window.innerWidth - 32);
    const bubbleLeft = Math.max(16, Math.min(left, window.scrollX + window.innerWidth - bw - 16));
    setBox({ top, left, width, height, bubbleTop: top + height + 12, bubbleLeft });
  }, [steps, i]);

  useLayoutEffect(() => { if (open) place(); }, [open, place]);

  useEffect(() => {
    document.body.classList.toggle('lt-open', open);
    return () => document.body.classList.remove('lt-open');
  }, [open]);

  useEffect(() => {
    if (!open) return;
    // Fonts and the hero video shift layout after first paint; re-measure.
    const t = setTimeout(place, 600);
    document.fonts?.ready.then(place);
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(place) : null;
    ro?.observe(document.body);
    window.addEventListener('resize', place);
    return () => { clearTimeout(t); ro?.disconnect(); window.removeEventListener('resize', place); };
  }, [open, place]);

  const goTo = (n) => {
    setI(n);
    setDrawKey((k) => k + 1);
    document.querySelector(steps[n].selector)?.scrollIntoView({
      behavior: reduce.current ? 'auto' : 'smooth',
      block: 'center',
    });
  };

  const next = () => {
    if (i === steps.length - 1) { stop('finish'); return; }
    ReactGA.event('landing_tour_next', { step: i + 2 });
    goTo(i + 1);
  };

  const stop = (reason = 'stop') => {
    ReactGA.event('landing_tour_end', { reason, step: i + 1 });
    writeDismissed(true);
    setOpen(false);
  };

  const replay = () => {
    writeDismissed(false);
    setOpen(true);
    goTo(0);
  };

  const step = steps[i];
  const last = i === steps.length - 1;

  return (
    <>
      {open && box && createPortal(
        <>
          <div
            className="lt-ring"
            aria-hidden="true"
            style={{ top: box.top, left: box.left, width: box.width, height: box.height }}
          >
            <div className="lt-ring__halo" />
            <svg key={drawKey} className="lt-ring__svg" preserveAspectRatio="none">
              <rect x="0" y="0" width="100%" height="100%" rx="12" pathLength="1" />
            </svg>
          </div>
          <div
            className="lt-bubble"
            aria-hidden="true"
            style={{ top: box.bubbleTop, left: box.bubbleLeft }}
          >
            <Mascot className="lt-bubble__mascot" />
            <div className="lt-bubble__say">
              <b>Michii says</b>
              <span>{step.say}</span>
            </div>
          </div>
        </>,
        document.body
      )}

      {open ? (
        <div className="lt-panel" role="region" aria-label="Guide playback">
          <div className="lt-panel__head"><span>Guide playback</span><span>This page</span></div>
          <div className="lt-panel__track">
            <span className="lt-panel__fill" style={{ width: `${(i / (steps.length - 1)) * 100}%` }} />
            {steps.map((s, k) => (
              <i key={s.selector} className={k < i ? 'done' : k === i ? 'cur' : ''} />
            ))}
          </div>
          <p className="lt-panel__step">Step {i + 1} of {steps.length}</p>
          <p className="lt-panel__txt" aria-live="polite">{step.panel}</p>
          <div className="lt-panel__ctrls">
            <button type="button" className="lt-panel__stop" onClick={() => stop()}>Stop</button>
            <button type="button" className="lt-panel__next" onClick={next}>{last ? 'Finish' : 'Next step'}</button>
          </div>
        </div>
      ) : (
        <button type="button" className="lt-replay" onClick={replay}>
          <i aria-hidden="true" />Replay the tour
        </button>
      )}
    </>
  );
};

export default LandingTour;
